/**
 * API Client - Gold Standard HTTP Client
 *
 * This is the definitive HTTP client for all frontend-backend communication.
 * All API calls should go through this client to ensure consistent:
 * - Authentication handling
 * - Error processing
 * - Request/response transformation
 * - Logging and debugging
 * - Retry logic
 * - Cache management
 */

import axios from "axios";
import { toast } from "sonner";
import authService from "../services/authService";
import { supabase } from "../../lib/supabase";
import logger from "./logger";

/**
 * API Client Configuration
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;

// Request deduplication cache
const pendingRequests = new Map();
const CACHE_DURATION = 1000; // 1 second cache for identical requests

/**
 * Convert relative upload URLs to absolute URLs
 * @param {string} url - The URL to convert
 * @returns {string} - Absolute URL
 */
export const getImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;

  const trimmed = url.trim();

  // Already absolute
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // DB-backed image route
  if (trimmed.startsWith("/api/upload/image/")) {
    return trimmed;
  }

  // Legacy public uploads
  if (trimmed.startsWith("/uploads/")) {
    return trimmed;
  }
  if (trimmed.startsWith("uploads/")) {
    return `/${trimmed}`;
  }
  if (trimmed.includes("/uploads/")) {
    const idx = trimmed.indexOf("/uploads/");
    return trimmed.slice(idx);
  }

  // As a last resort, return unchanged
  return trimmed;
};

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  // NOTE: We default to JSON, but will dynamically remove this header for
  // FormData uploads in the request interceptor so the browser can set the
  // correct multipart/form-data boundary.
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor - Add authentication and logging
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Add request ID for tracking
    config.metadata = { startTime: Date.now() };

    // Create request key for deduplication (GET requests only)
    if (config.method === "get") {
      const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;

      // Check if identical request is already pending
      if (pendingRequests.has(requestKey)) {
        const pendingRequest = pendingRequests.get(requestKey);
        if (Date.now() - pendingRequest.timestamp < CACHE_DURATION) {
          // Skip deduplication for now to avoid interceptor issues
          pendingRequests.delete(requestKey); // Clear stale entry
        }
      }

      // Store this request as pending
      config.requestKey = requestKey;
    }

    // Add authentication token only for endpoints that need it
    // - ALWAYS include for /api/admin
    // - For casino: attach for all mutating requests and protected GETs; only skip for truly public GETs
    const url = typeof config.url === "string" ? config.url : "";
    const method = (config.method || "get").toLowerCase();
    const isMutating = ["post", "put", "patch", "delete"].includes(method);

    const isAdminRoute = url.startsWith("/api/admin");

    // Public casino GET endpoints that do not require auth
    const isPublicCasinoGet =
      method === "get" &&
      (url.startsWith("/api/casino/games/catalog") ||
        url.startsWith("/api/casino/games/category") ||
        url.startsWith("/api/casino/games/search") ||
        // Game details GET: /api/casino/games/:gameCode
        /^\/api\/casino\/games\/[A-Za-z0-9_-]+$/.test(url));

    const shouldAttachAuth = isAdminRoute || isMutating || !isPublicCasinoGet;

    if (shouldAttachAuth) {
      try {
        const { session } = await authService.getSession();
        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        } else if (import.meta.env.DEV) {
          console.warn(
            "[ApiClient] No session/token available for request:",
            config.url,
          );
        }
      } catch (error) {
        // Only log auth token failures in development
        if (import.meta.env.DEV) {
          console.warn("[ApiClient] Failed to get auth token:", error.message);
        }
      }
    }

    // If we're sending FormData, let the browser/axios set the correct
    // multipart Content-Type with boundary instead of forcing JSON.
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }

    return config;
  },
  (error) => {
    // Only log request interceptor errors in development
    if (import.meta.env.DEV) {
      console.error("[ApiClient] Request interceptor error:", error);
    }
    return Promise.reject(error);
  },
);

/**
 * Response interceptor - Handle responses and errors
 */
apiClient.interceptors.response.use(
  (response) => {
    // Clean up pending request cache
    if (response.config.requestKey) {
      pendingRequests.delete(response.config.requestKey);
    }

    return response;
  },
  async (error) => {
    // Clean up pending request cache
    if (error.config?.requestKey) {
      pendingRequests.delete(error.config.requestKey);
    }

    // Development logging can be enabled via browser dev tools if needed

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Attempt a one-time retry after fetching a fresh session token
      try {
        const originalRequest = error.config || {};
        if (!originalRequest._retry401) {
          originalRequest._retry401 = true;

          // If backend indicates token is expired, refresh it first
          if (error.response?.headers?.["x-token-expired"] === "true") {
            const { data, error: refreshError } =
              await supabase.auth.refreshSession();
            if (refreshError || !data?.session?.access_token) {
              throw new Error("Failed to refresh token");
            }
          }

          const { session } = await authService.getSession();
          if (session?.access_token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
            return apiClient.request(originalRequest);
          }
        }
      } catch (refreshErr) {
        // Token refresh / retry failed. Log it (previously swallowed silently)
        // and proceed to suspension/other handling below.
        logger.error("[ApiClient] Token refresh/retry after 401 failed", {
          url: error.config?.url,
          message: refreshErr?.message,
        });
      }

      // Check if this is a suspension-related 401
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || "";
      const lower = (errorMessage || "").toLowerCase();
      const isSuspended =
        lower.includes("suspended") ||
        lower.includes("banned") ||
        lower.includes("inactive");

      if (isSuspended) {
        // User has been suspended - force logout and show suspension message
        try {
          await authService.signOut();

          // Show suspension message
          toast.error(errorMessage, { duration: 10000 });

          // Redirect to login page
          window.location.href = "/login";
        } catch (logoutError) {
          console.error("Failed to handle suspension logout:", logoutError);
          // Force page reload as fallback
          window.location.reload();
        }
      }
    }

    const status = error.response?.status;
    const responseData = error.response?.data;
    const serverMessage =
      responseData?.error || responseData?.message || undefined;

    // PHONE_REQUIRED: a free-Cluck-Bucks claim (wheel/calendar/VIP/AMOE) or a
    // redemption needs the one-phone-per-player check. Announce it so the
    // contextual PhoneGate sheet opens; the error still rejects so the calling
    // surface can settle its own state.
    if (status === 403 && responseData?.errorCode === "PHONE_REQUIRED") {
      try {
        window.dispatchEvent(
          new CustomEvent("cluck:phone-required", {
            detail: { message: serverMessage },
          }),
        );
      } catch {
        /* announcement is best-effort */
      }
    }

    if (status === 403) {
      // Forbidden - insufficient permissions. Surface as telemetry instead of
      // swallowing, so an unexpected permission failure is visible.
      logger.error("[ApiClient] 403 Forbidden", {
        url: error.config?.url,
        method: error.config?.method,
        message: serverMessage,
      });
    }

    if (status >= 500) {
      // Server error - log so 5xx failures are not invisible.
      logger.error("[ApiClient] Server error", {
        url: error.config?.url,
        method: error.config?.method,
        status,
        message: serverMessage,
      });
    }

    // Preserve the original Error (keeps the stack and Error prototype) while
    // attaching API context. Consumers (and normalizeQueryError) rely on
    // .status / .data / .message being present on the rejected value.
    error.isApiError = true;
    error.status = status;
    error.data = responseData;
    if (serverMessage) {
      error.message = serverMessage;
    }

    return Promise.reject(error);
  },
);

/**
 * Retry logic for failed requests
 */
const retryRequest = async (fn, retries = MAX_RETRIES) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && shouldRetry(error)) {
      await delay(1000); // Wait 1 second before retry
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

/**
 * Determine if a request should be retried
 */
const shouldRetry = (error) => {
  // Retry on network errors or 5xx server errors
  return (
    !error.response ||
    (error.response.status >= 500 && error.response.status < 600)
  );
};

/**
 * Delay utility for retries
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Enhanced API client with retry logic and convenience methods
 */
const enhancedApiClient = {
  /**
   * GET request with retry logic
   */
  async get(url, config = {}) {
    return retryRequest(() => apiClient.get(url, config));
  },

  /**
   * POST request with retry logic
   */
  async post(url, data = {}, config = {}) {
    return retryRequest(() => apiClient.post(url, data, config));
  },

  /**
   * PUT request with retry logic
   */
  async put(url, data = {}, config = {}) {
    return retryRequest(() => apiClient.put(url, data, config));
  },

  /**
   * PATCH request with retry logic
   */
  async patch(url, data = {}, config = {}) {
    return retryRequest(() => apiClient.patch(url, data, config));
  },

  /**
   * DELETE request with retry logic
   */
  async delete(url, config = {}) {
    return retryRequest(() => apiClient.delete(url, config));
  },

  /**
   * Upload file with progress tracking
   */
  async upload(url, file, onProgress = null) {
    const formData = new FormData();
    formData.append("file", file);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      };
    }

    return apiClient.post(url, formData, config);
  },

  /**
   * Download file
   */
  async download(url, filename = null) {
    const response = await apiClient.get(url, {
      responseType: "blob",
    });

    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return response;
  },

  /**
   * Health check endpoint
   */
  async healthCheck() {
    try {
      const response = await this.get("/health");
      return response.data;
    } catch (error) {
      console.error("[ApiClient] Health check failed:", error);
      throw error;
    }
  },

  /**
   * Get API client instance for advanced usage
   */
  getInstance() {
    return apiClient;
  },
};

/**
 * Error types for better error handling
 */
export const ApiErrorTypes = {
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

/**
 * Utility to classify API errors
 */
export const classifyApiError = (error) => {
  if (!error.response) {
    return error.code === "ECONNABORTED"
      ? ApiErrorTypes.TIMEOUT_ERROR
      : ApiErrorTypes.NETWORK_ERROR;
  }

  const status = error.response.status;

  if (status === 401) return ApiErrorTypes.UNAUTHORIZED;
  if (status === 403) return ApiErrorTypes.FORBIDDEN;
  if (status === 404) return ApiErrorTypes.NOT_FOUND;
  if (status === 422) return ApiErrorTypes.VALIDATION_ERROR;
  if (status >= 500) return ApiErrorTypes.SERVER_ERROR;

  return ApiErrorTypes.UNKNOWN_ERROR;
};

export { enhancedApiClient as apiClient };
export default enhancedApiClient;
